'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ErrorBar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { Lock, Sparkles, Users, Stethoscope } from 'lucide-react';

const METRIC_KEYS = ['energy', 'recovery', 'consistency', 'sleep', 'community'];
const DONUT_COLORS = ['#22d3ee', '#334155'];

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/**
 * Deterministic composite indices from guest answers (not clinical measurements).
 * Accepts single values or multi-select arrays (most restrictive feeling + all goals).
 */
export function computeGuestMetrics({ goal, goals, feeling, feelings, age }) {
  const ageNum = Number(age) || 40;
  const goalList = Array.isArray(goals) && goals.length ? goals : goal ? [goal] : [];
  const feelingList =
    Array.isArray(feelings) && feelings.length ? feelings : feeling ? [feeling] : [];
  const base = {
    energy: 58,
    recovery: 56,
    consistency: 54,
    sleep: 57,
    community: 46,
  };

  const applyFeeling = (f) => {
    if (f === 'energetic' || f === 'motivated') {
      base.energy += 14;
      base.recovery += 8;
      base.consistency += 6;
    } else if (f === 'good') {
      base.energy += 12;
      base.recovery += 8;
      base.consistency += 6;
      base.sleep += 4;
    } else if (f === 'okay') {
      base.energy += 2;
    } else if (f === 'sleepy') {
      base.energy -= 10;
      base.sleep -= 12;
    } else if (f === 'tired') {
      base.energy -= 16;
      base.recovery -= 12;
      base.sleep -= 10;
    } else if (f === 'sore') {
      base.energy -= 8;
      base.recovery -= 18;
      base.consistency -= 6;
    } else if (f === 'stressed' || f === 'anxious') {
      base.energy -= 12;
      base.sleep -= 16;
      base.community += 8;
    }
  };
  feelingList.forEach(applyFeeling);

  const applyGoal = (g) => {
    if (g === 'mental_wellbeing') {
      base.sleep += 6;
      base.community += 10;
    } else if (g === 'more_energy') {
      base.energy += 10;
    } else if (g === 'move_better') {
      base.recovery += 8;
      base.consistency += 5;
    } else if (g === 'lose_weight' || g === 'gain_muscle') {
      base.consistency += 8;
    } else if (g === 'eat_better') {
      base.consistency += 6;
      base.energy += 3;
    } else if (g === 'sleep_better') {
      base.sleep += 10;
      base.recovery += 4;
    } else if (g === 'longevity') {
      base.recovery += 5;
      base.sleep += 3;
    }
  };
  goalList.forEach(applyGoal);

  // Mild age adjustment (honest heuristic for display only).
  if (ageNum >= 55) {
    base.recovery -= 4;
    base.sleep -= 3;
  } else if (ageNum <= 30) {
    base.energy += 4;
  }

  for (const key of METRIC_KEYS) {
    base[key] = Math.round(clamp(base[key], 18, 92));
  }
  return base;
}

function compositeScore(metrics) {
  const values = METRIC_KEYS.map((k) => metrics[k]);
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

function buildProjection(metrics) {
  const start = compositeScore(metrics);
  const points = [];
  for (let day = 0; day <= 14; day += 1) {
    const guestFlat = start + Math.sin(day / 3) * 1.2;
    const withAccount = start + day * 1.35 + Math.sin(day / 2.4) * 1.8;
    points.push({
      day: `D${day}`,
      guest: Math.round(clamp(guestFlat, 15, 95)),
      adaptive: Math.round(clamp(withAccount, 15, 98)),
    });
  }
  return points;
}

const tooltipStyle = {
  background: '#0f172a',
  border: '1px solid rgba(51,65,85,0.85)',
  borderRadius: '12px',
  color: '#e2e8f0',
  fontSize: 12,
};

function FigureFrame({ caption, children }) {
  return (
    <figure className="rounded-xl border border-slate-700/80 bg-slate-950/70 p-3">
      <div className="h-[200px] w-full sm:h-[220px]">{children}</div>
      <figcaption className="mt-2 border-t border-slate-800 pt-2 text-[10px] leading-relaxed text-slate-400">
        {caption}
      </figcaption>
    </figure>
  );
}

export default function EvolutionPreviewPanel({
  goal = 'longevity',
  goals,
  feeling = 'good',
  feelings,
  age = 40,
}) {
  const t = useTranslations('QuickHook.evolution');

  const metrics = useMemo(
    () => computeGuestMetrics({ goal, goals, feeling, feelings, age }),
    [goal, goals, feeling, feelings, age]
  );

  const radarData = useMemo(
    () =>
      METRIC_KEYS.map((key) => ({
        axis: t(`metrics.${key}`),
        score: metrics[key],
        fullMark: 100,
      })),
    [metrics, t]
  );

  const barData = useMemo(
    () =>
      METRIC_KEYS.map((key) => {
        const value = metrics[key];
        const err = Math.max(3, Math.round((100 - value) * 0.08));
        return {
          name: t(`metricsShort.${key}`),
          value,
          error: [err, err],
        };
      }),
    [metrics, t]
  );

  const readiness = compositeScore(metrics);
  const donutData = useMemo(
    () => [
      { name: t('readinessFill'), value: readiness },
      { name: t('readinessGap'), value: 100 - readiness },
    ],
    [readiness, t]
  );

  const projection = useMemo(() => buildProjection(metrics), [metrics]);

  return (
    <div className="space-y-3 rounded-2xl border border-amber-400/25 bg-gradient-to-br from-slate-950/90 via-slate-900/80 to-cyan-950/40 p-4">
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 shrink-0 text-amber-300" size={16} aria-hidden />
        <div>
          <p className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-amber-300">
            <Lock size={11} aria-hidden />
            {t('badge')}
          </p>
          <h5 className="mt-1 text-base font-black text-white">{t('title')}</h5>
          <p className="mt-1 text-xs leading-relaxed text-slate-300">{t('description')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <FigureFrame caption={t('figRadar')}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} cx="50%" cy="52%" outerRadius="72%">
              <PolarGrid stroke="rgba(100,116,139,0.45)" />
              <PolarAngleAxis dataKey="axis" tick={{ fill: '#94a3b8', fontSize: 9 }} />
              <PolarRadiusAxis
                angle={30}
                domain={[0, 100]}
                tick={{ fill: '#64748b', fontSize: 9 }}
                axisLine={false}
              />
              <Radar
                name={t('locked')}
                dataKey="score"
                stroke="#22d3ee"
                fill="#22d3ee"
                fillOpacity={0.28}
                strokeWidth={2}
              />
              <Tooltip contentStyle={tooltipStyle} />
            </RadarChart>
          </ResponsiveContainer>
        </FigureFrame>

        <FigureFrame caption={t('figBars')}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
              <CartesianGrid stroke="rgba(51,65,85,0.35)" strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 10 }} />
              <YAxis
                type="category"
                dataKey="name"
                width={72}
                tick={{ fill: '#94a3b8', fontSize: 10 }}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" fill="#38bdf8" radius={[0, 4, 4, 0]} barSize={14}>
                <ErrorBar dataKey="error" width={4} stroke="#94a3b8" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </FigureFrame>

        <FigureFrame caption={t('figDonut')}>
          <div className="relative h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="58%"
                  outerRadius="78%"
                  startAngle={90}
                  endAngle={-270}
                  stroke="none"
                >
                  {donutData.map((entry, index) => (
                    <Cell key={entry.name} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <p className="text-2xl font-black text-white">{readiness}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                {t('readinessLabel')}
              </p>
            </div>
          </div>
        </FigureFrame>

        <FigureFrame caption={t('figTrajectory')}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={projection} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid stroke="rgba(51,65,85,0.35)" strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 10 }} interval={2} />
              <YAxis domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 10 }} width={28} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 10, color: '#94a3b8' }} />
              <Line
                type="monotone"
                dataKey="guest"
                name={t('seriesGuest')}
                stroke="#64748b"
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="adaptive"
                name={t('seriesAdaptive')}
                stroke="#34d399"
                strokeWidth={2.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </FigureFrame>
      </div>

      <p className="text-[11px] leading-relaxed text-slate-400">{t('methodsNote')}</p>
      <p className="text-[11px] leading-relaxed text-slate-400">{t('adaptiveNote')}</p>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3 text-left">
          <p className="mb-1 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wide text-cyan-300">
            <Users size={13} aria-hidden />
            {t('communityTitle')}
          </p>
          <p className="text-xs leading-relaxed text-slate-300">{t('communityBody')}</p>
        </div>
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 text-left">
          <p className="mb-1 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wide text-emerald-300">
            <Stethoscope size={13} aria-hidden />
            {t('careTitle')}
          </p>
          <p className="text-xs leading-relaxed text-slate-300">{t('careBody')}</p>
        </div>
      </div>
    </div>
  );
}
